import { Capacitor, registerPlugin, type PluginListenerHandle } from '@capacitor/core';
import { Camera, CameraDirection, MediaTypeSelection } from '@capacitor/camera';

// Native photo library helper (ios/App/App/PhotoScanPlugin.swift). Every image it
// returns is already downsampled off the main thread.
interface NativePickedImage { dataUrl: string; takenAt?: string; assetId?: string }
export interface PhotoScanCandidate { id: string; takenAt?: string; label: string; confidence: number; thumbDataUrl?: string }
export interface PhotoScanProgress { scanned: number; total: number; found: number; candidates: PhotoScanCandidate[]; done?: boolean }
export interface PhotoScanResult {
  status: string;
  total: number;
  scanned: number;
  cancelled?: boolean;
  limitReached?: boolean;
  candidates: PhotoScanCandidate[];
}
// filter:false skips the on-device classifier and streams a thumbnail of every photo
// (cloud detection, docs/gallery-scan.md).
export interface PhotoScanOptions {
  days?: number;
  maxCandidates?: number;
  minConfidence?: number;
  thumbSize?: number;
  thumbQuality?: number;
  filter?: boolean;
  excludeIds?: string[];
}
export interface PickedPhoto { dataUrl: string; takenAt: Date | null; assetId?: string; file?: File }

interface PhotoScanPlugin {
  pickImage(options: { maxSize?: number; quality?: number }): Promise<NativePickedImage & { cancelled?: boolean }>;
  pickImages(options: { limit?: number; maxSize?: number; quality?: number }): Promise<{ images: NativePickedImage[]; cancelled?: boolean }>;
  accessStatus(): Promise<{ status: string }>;
  requestAccess(): Promise<{ status: string }>;
  presentLimitedPicker(): Promise<{ count: number }>;
  scan(options: PhotoScanOptions): Promise<PhotoScanResult>;
  countImages(options: { days?: number; excludeIds?: string[] }): Promise<{ status: string; total: number }>;
  cancelScan(): Promise<void>;
  getImage(options: { id: string; maxSize?: number; quality?: number }): Promise<{ dataUrl: string; width: number; height: number }>;
  addListener(event: 'scanProgress', listener: (progress: PhotoScanProgress) => void): Promise<PluginListenerHandle>;
}

const PhotoScan = registerPlugin<PhotoScanPlugin>('PhotoScan');
const nativePhotoScan = () => Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('PhotoScan');

// Reads EXIF DateTimeOriginal (0x9003) from a JPEG so gallery photos keep their capture time.
async function exifCaptureDate(file: Blob): Promise<Date | null> {
  try {
    const view = new DataView(await file.slice(0, 256 * 1024).arrayBuffer());
    if (view.getUint16(0) !== 0xffd8) return null;
    let offset = 2;
    while (offset < view.byteLength - 4) {
      const marker = view.getUint16(offset);
      const size = view.getUint16(offset + 2);
      if (marker === 0xffe1 && view.getUint32(offset + 4) === 0x45786966) {
        const tiff = offset + 10;
        const little = view.getUint16(tiff) === 0x4949;
        const u16 = (o: number) => view.getUint16(o, little);
        const u32 = (o: number) => view.getUint32(o, little);
        const findTag = (ifd: number, tag: number): number | null => {
          const count = u16(ifd);
          for (let i = 0; i < count; i += 1) {
            const entry = ifd + 2 + i * 12;
            if (u16(entry) === tag) return u32(entry + 8);
          }
          return null;
        };
        const exifIfd = findTag(tiff + u32(tiff + 4), 0x8769);
        if (exifIfd === null) return null;
        const valueOffset = findTag(tiff + exifIfd, 0x9003);
        if (valueOffset === null) return null;
        let text = '';
        for (let i = 0; i < 19; i += 1) text += String.fromCharCode(view.getUint8(tiff + valueOffset + i));
        const m = text.match(/^(\d{4}):(\d{2}):(\d{2}) (\d{2}):(\d{2})/);
        return m ? new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]) : null;
      }
      offset += 2 + size;
    }
  } catch (_) {}
  return null;
}

function parseDate(value?: string | null): Date | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

// Capture time of a web-picked file: EXIF first, lastModified only when it clearly
// is not "just now" (picker exports stamp the export time).
async function fileCaptureDate(file: File): Promise<Date | null> {
  const exif = await exifCaptureDate(file);
  if (exif) return exif;
  const modified = file.lastModified ? new Date(file.lastModified) : null;
  return modified && Date.now() - modified.getTime() > 2 * 60000 ? modified : null;
}

async function dataUrlToFile(dataUrl: string, name = 'jidlo.jpg'): Promise<File> {
  const blob = await (await fetch(dataUrl)).blob();
  return new File([blob], name, { type: blob.type || 'image/jpeg' });
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error || new Error('Obrázek se nepodařilo přečíst.'));
    reader.readAsDataURL(blob);
  });
}

// Downscales an image to a JPEG data URL. Decoding happens off the main thread
// (createImageBitmap) and encoding in an OffscreenCanvas where the engine has one.
async function downscaleImage(source: Blob | string, maxSize = 1024, quality = 0.8): Promise<string> {
  const blob = typeof source === 'string' ? await (await fetch(source)).blob() : source;
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(blob);
      const scale = Math.min(1, maxSize / Math.max(bitmap.width, bitmap.height));
      const width = Math.max(1, Math.round(bitmap.width * scale));
      const height = Math.max(1, Math.round(bitmap.height * scale));
      if (typeof OffscreenCanvas === 'function') {
        const canvas = new OffscreenCanvas(width, height);
        canvas.getContext('2d')!.drawImage(bitmap, 0, 0, width, height);
        bitmap.close();
        return blobToDataUrl(await canvas.convertToBlob({ type: 'image/jpeg', quality }));
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d')!.drawImage(bitmap, 0, 0, width, height);
      bitmap.close();
      return canvas.toDataURL('image/jpeg', quality);
    } catch (_) {
      // fall through to <img> decoding (older engines, unusual formats)
    }
  }
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(blob);
    img.onload = () => {
      const scale = Math.min(1, maxSize / Math.max(img.naturalWidth, img.naturalHeight));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
      canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
      canvas.getContext('2d')!.drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Obrázek se nepodařilo načíst.')); };
    img.src = url;
  });
}

function pickFiles(multiple: boolean): Promise<File[]> {
  return new Promise(resolve => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = multiple;
    input.hidden = true;
    document.body.append(input);
    const finish = (files: File[]) => { input.remove(); resolve(files); };
    input.addEventListener('change', () => finish(Array.from(input.files || [])), { once: true });
    input.addEventListener('cancel', () => finish([]), { once: true });
    input.click();
  });
}

window.APP_CAMERA = {
  // Gallery pick: returns the file plus when the photo was taken (Photos metadata / EXIF).
  // On iOS this uses the system picker + native downsampling; the Camera plugin gallery
  // decoded the full-size original on the main thread (iOS hang banner in dev builds).
  async pickFromGallery(): Promise<{ file: File; takenAt: Date | null; assetId?: string } | null> {
    if (nativePhotoScan()) {
      const picked = await PhotoScan.pickImage({ maxSize: 1600, quality: 0.8 });
      if (picked.cancelled || !picked.dataUrl) return null;
      return { file: await dataUrlToFile(picked.dataUrl), takenAt: parseDate(picked.takenAt), assetId: picked.assetId };
    }
    if (Capacitor.isNativePlatform()) {
      try {
        const result = await Camera.chooseFromGallery({ mediaType: MediaTypeSelection.Photo, limit: 1, includeMetadata: true, quality: 85, targetWidth: 1600, targetHeight: 1600, correctOrientation: true });
        const media = result.results?.[0];
        if (!media?.webPath) return null;
        const blob = await (await fetch(media.webPath)).blob();
        const file = new File([blob], 'jidlo.jpg', { type: blob.type || 'image/jpeg' });
        const created = parseDate(media.metadata?.creationDate);
        return { file, takenAt: created || await exifCaptureDate(blob) };
      } catch (error) {
        const detail = error as { message?: string };
        if (/cancel/i.test(detail.message || '')) return null;
        throw error;
      }
    }
    const [file] = await pickFiles(false);
    if (!file) return null;
    return { file, takenAt: await fileCaptureDate(file) };
  },
  async pickFoodPhoto(): Promise<File | null> {
    if (Capacitor.isNativePlatform()) return this.takePhoto();
    const [file] = await pickFiles(false);
    return file || null;
  },
  async takePhoto(): Promise<File | null> {
    try {
      const photo = await Camera.takePhoto({
        quality: 85,
        targetWidth: 1600,
        targetHeight: 1600,
        correctOrientation: true,
        cameraDirection: CameraDirection.Rear,
        saveToGallery: false,
        webUseInput: true,
      });
      if (!photo.webPath) throw new Error('Fotoaparát nevrátil fotografii.');
      const response = await fetch(photo.webPath);
      if (!response.ok) throw new Error('Fotografii se nepodařilo načíst.');
      const blob = await response.blob();
      return new File([blob], 'jidlo.jpg', { type: blob.type || 'image/jpeg' });
    } catch (error) {
      const detail = error as { code?: string; message?: string };
      if (detail.code === 'OS-PLUG-CAMR-0006' || /cancel/i.test(detail.message || '')) return null;
      if (detail.code === 'OS-PLUG-CAMR-0003') {
        throw new Error('Povolte Jídelníčkovači fotoaparát v Nastavení iPhonu.');
      }
      throw error;
    }
  },
  downscale: downscaleImage,
};

// Gallery scan ("Projet galerii"): on iOS the library is scanned natively with an
// on-device food classifier; elsewhere the user multi-selects photos.
window.APP_PHOTO_SCAN = {
  get native() { return nativePhotoScan(); },
  async status() {
    return nativePhotoScan() ? (await PhotoScan.accessStatus()).status : 'unsupported';
  },
  async requestAccess() {
    return nativePhotoScan() ? (await PhotoScan.requestAccess()).status : 'unsupported';
  },
  async presentLimitedPicker() {
    if (nativePhotoScan()) await PhotoScan.presentLimitedPicker();
  },
  async scan(options, onProgress) {
    if (!nativePhotoScan()) throw new Error('Procházení galerie je jen v aplikaci pro iPhone.');
    const handle = onProgress ? await PhotoScan.addListener('scanProgress', onProgress) : null;
    try {
      return await PhotoScan.scan(options);
    } finally {
      await handle?.remove();
    }
  },
  async count(options) {
    if (!nativePhotoScan()) return 0;
    return (await PhotoScan.countImages(options)).total;
  },
  async cancel() {
    if (nativePhotoScan()) await PhotoScan.cancelScan();
  },
  async getImage(id, maxSize = 1024) {
    return (await PhotoScan.getImage({ id, maxSize, quality: 0.8 })).dataUrl;
  },
  // Manual multi-select (web, or iOS without library access). Images come back as
  // ~1024 px JPEG data URLs with the best known capture time.
  async pickPhotos(limit = 60) {
    if (nativePhotoScan()) {
      const result = await PhotoScan.pickImages({ limit, maxSize: 1024, quality: 0.8 });
      return (result.images || []).map(image => ({ dataUrl: image.dataUrl, takenAt: parseDate(image.takenAt), assetId: image.assetId }));
    }
    const files = (await pickFiles(true)).slice(0, limit);
    const picked: PickedPhoto[] = [];
    for (const file of files) {
      try {
        picked.push({ dataUrl: await downscaleImage(file, 1024, 0.8), takenAt: await fileCaptureDate(file), file });
      } catch (error) {
        console.warn('[gallery] skipped unreadable file', file.name, error);
      }
    }
    return picked;
  },
};
